import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import { Add, Edit, Delete, Download, UploadFile } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useToast } from '../../contexts/ToastContext';
import { normalizePartNumber } from '../../utils/partNumber';
import {
  downloadCsvFile,
  findCsvColumnIndex,
  parseCsvLine,
  parseOptionalCsvDate
} from '../../utils/csvHelpers';

const SPARES_CSV_HEADERS = ['Part Number', 'Quantity', 'Description', 'Remarks'];
const CONSUMABLES_CSV_HEADERS = ['Part Number', 'Quantity', 'Description', 'Expiry Date', 'Remarks'];
const TOOLS_CSV_HEADERS = ['Part Number', 'Quantity', 'Item Name', 'Due Date', 'Remarks'];

const EMPTY_ITEMS = {
  requiredSpares: [],
  requiredConsumables: [],
  requiredToolsEquipment: []
};

const countItems = (items = EMPTY_ITEMS) =>
  (items.requiredSpares?.length || 0)
  + (items.requiredConsumables?.length || 0)
  + (items.requiredToolsEquipment?.length || 0);

/**
 * Manual entry + CSV upload for spares, consumables, and tools on an AMP check.
 */
const AmpCheckRequiredItemsEditor = ({
  requiredSpares = [],
  requiredConsumables = [],
  requiredToolsEquipment = [],
  onChange,
  checkLabel = 'check'
}) => {
  const [tab, setTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const csvUploadInputRef = useRef(null);
  const { showToast } = useToast();

  const items = {
    requiredSpares: requiredSpares || [],
    requiredConsumables: requiredConsumables || [],
    requiredToolsEquipment: requiredToolsEquipment || []
  };

  const emitChange = (next) => {
    onChange({
      requiredSpares: next.requiredSpares || [],
      requiredConsumables: next.requiredConsumables || [],
      requiredToolsEquipment: next.requiredToolsEquipment || []
    });
  };

  const categoryKey = tab === 0 ? 'requiredSpares' : tab === 1 ? 'requiredConsumables' : 'requiredToolsEquipment';
  const categoryLabel = tab === 0 ? 'Spares' : tab === 1 ? 'Consumables' : 'Tools';
  const currentRows = items[categoryKey] || [];

  const defaultFormForTab = () => {
    if (tab === 0) {
      return { itemName: '', partNumber: '', quantity: 1, remarks: '' };
    }
    if (tab === 1) {
      return { itemName: '', partNumberOrSpecification: '', quantity: 1, expiry: null, remarks: '' };
    }
    return { itemName: '', partNumber: '', quantity: 1, due: dayjs().add(1, 'year'), remarks: '' };
  };

  const handleAdd = () => {
    setEditingIndex(null);
    setEditFormData(defaultFormForTab());
    setEditDialogOpen(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    const row = currentRows[index];
    if (tab === 1) {
      setEditFormData({
        ...row,
        expiry: row.expiry ? dayjs(row.expiry) : null
      });
    } else if (tab === 2) {
      setEditFormData({
        ...row,
        quantity: row.quantity || 1,
        due: row.due ? dayjs(row.due) : dayjs().add(1, 'year')
      });
    } else {
      setEditFormData({ ...row });
    }
    setEditDialogOpen(true);
  };

  const handleDelete = (index) => {
    const nextRows = currentRows.filter((_, i) => i !== index);
    emitChange({ ...items, [categoryKey]: nextRows });
  };

  const handleSaveEdit = () => {
    if (tab === 0) {
      const partNumber = normalizePartNumber(editFormData.partNumber);
      if (!partNumber) {
        showToast('Part number is required', 'error');
        return;
      }
      const row = {
        itemName: String(editFormData.itemName || partNumber).trim(),
        partNumber,
        quantity: Math.max(1, Number(editFormData.quantity) || 1),
        remarks: String(editFormData.remarks || '').trim()
      };
      const nextRows = [...currentRows];
      if (editingIndex === null) nextRows.push(row);
      else nextRows[editingIndex] = row;
      emitChange({ ...items, requiredSpares: nextRows });
    } else if (tab === 1) {
      const pn = normalizePartNumber(editFormData.partNumberOrSpecification);
      if (!pn) {
        showToast('Part number / specification is required', 'error');
        return;
      }
      const row = {
        itemName: String(editFormData.itemName || pn).trim(),
        partNumberOrSpecification: pn,
        quantity: Math.max(1, Number(editFormData.quantity) || 1),
        remarks: String(editFormData.remarks || '').trim()
      };
      if (editFormData.expiry) {
        row.expiry = editFormData.expiry.toISOString();
      }
      const nextRows = [...currentRows];
      if (editingIndex === null) nextRows.push(row);
      else nextRows[editingIndex] = row;
      emitChange({ ...items, requiredConsumables: nextRows });
    } else {
      const partNumber = normalizePartNumber(editFormData.partNumber);
      if (!partNumber) {
        showToast('Part number is required', 'error');
        return;
      }
      const row = {
        itemName: String(editFormData.itemName || partNumber).trim(),
        partNumber,
        quantity: Math.max(1, Number(editFormData.quantity) || 1),
        remarks: String(editFormData.remarks || '').trim()
      };
      if (editFormData.due) {
        row.due = editFormData.due.toISOString();
      }
      const nextRows = [...currentRows];
      if (editingIndex === null) nextRows.push(row);
      else nextRows[editingIndex] = row;
      emitChange({ ...items, requiredToolsEquipment: nextRows });
    }
    setEditDialogOpen(false);
    setEditingIndex(null);
  };

  const handleDownloadCsv = () => {
    if (tab === 0) {
      const rows = items.requiredSpares.map((row) => ({
        'Part Number': row.partNumber || '',
        Quantity: row.quantity || 1,
        Description: row.itemName || '',
        Remarks: row.remarks || ''
      }));
      downloadCsvFile(`amp-check-${checkLabel}-spares.csv`, SPARES_CSV_HEADERS, rows);
    } else if (tab === 1) {
      const rows = items.requiredConsumables.map((row) => ({
        'Part Number': row.partNumberOrSpecification || '',
        Quantity: row.quantity || 1,
        Description: row.itemName || '',
        'Expiry Date': row.expiry ? dayjs(row.expiry).format('YYYY-MM-DD') : '',
        Remarks: row.remarks || ''
      }));
      downloadCsvFile(`amp-check-${checkLabel}-consumables.csv`, CONSUMABLES_CSV_HEADERS, rows);
    } else {
      const rows = items.requiredToolsEquipment.map((row) => ({
        'Part Number': row.partNumber || '',
        Quantity: row.quantity || 1,
        'Item Name': row.itemName || '',
        'Due Date': row.due ? dayjs(row.due).format('YYYY-MM-DD') : '',
        Remarks: row.remarks || ''
      }));
      downloadCsvFile(`amp-check-${checkLabel}-tools.csv`, TOOLS_CSV_HEADERS, rows);
    }
  };

  const handleUploadCsvClick = () => {
    csvUploadInputRef.current?.click();
  };

  const handleUploadCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        showToast('CSV has no data rows to import', 'error');
        return;
      }
      const header = parseCsvLine(lines[0]);
      let imported = [];

      if (tab === 0) {
        const pnIdx = findCsvColumnIndex(header, ['part number', 'partnumber', 'pn']);
        const qtyIdx = findCsvColumnIndex(header, ['quantity', 'qty']);
        const descIdx = findCsvColumnIndex(header, ['description', 'desc', 'item name', 'itemname']);
        const remarksIdx = findCsvColumnIndex(header, ['remarks', 'remark']);
        if (pnIdx < 0 || qtyIdx < 0) {
          showToast('CSV must include "Part Number" and "Quantity" columns', 'error');
          return;
        }
        imported = lines.slice(1).map((line) => parseCsvLine(line)).map((columns) => {
          const partNumber = normalizePartNumber(columns[pnIdx]);
          if (!partNumber) return null;
          const qtyRaw = Number(columns[qtyIdx]);
          const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
          const description = descIdx >= 0 ? String(columns[descIdx] || '').trim() : '';
          return {
            partNumber,
            quantity,
            itemName: description || partNumber,
            remarks: remarksIdx >= 0 ? String(columns[remarksIdx] || '').trim() : ''
          };
        }).filter(Boolean);
      } else if (tab === 1) {
        const pnIdx = findCsvColumnIndex(header, ['part number', 'partnumber', 'pn', 'specification']);
        const qtyIdx = findCsvColumnIndex(header, ['quantity', 'qty']);
        const descIdx = findCsvColumnIndex(header, ['description', 'desc', 'item name', 'itemname']);
        const expiryIdx = findCsvColumnIndex(header, ['expiry date', 'expiry', 'exp']);
        const remarksIdx = findCsvColumnIndex(header, ['remarks', 'remark']);
        if (pnIdx < 0 || qtyIdx < 0) {
          showToast('CSV must include "Part Number" and "Quantity" columns', 'error');
          return;
        }
        imported = lines.slice(1).map((line) => parseCsvLine(line)).map((columns) => {
          const partNumberOrSpecification = normalizePartNumber(columns[pnIdx]);
          if (!partNumberOrSpecification) return null;
          const qtyRaw = Number(columns[qtyIdx]);
          const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
          const description = descIdx >= 0 ? String(columns[descIdx] || '').trim() : '';
          const row = {
            partNumberOrSpecification,
            quantity,
            itemName: description || partNumberOrSpecification,
            remarks: remarksIdx >= 0 ? String(columns[remarksIdx] || '').trim() : ''
          };
          if (expiryIdx >= 0) {
            const expiry = parseOptionalCsvDate(columns[expiryIdx]);
            if (expiry) row.expiry = expiry.toISOString();
          }
          return row;
        }).filter(Boolean);
      } else {
        const pnIdx = findCsvColumnIndex(header, ['part number', 'partnumber', 'pn']);
        const qtyIdx = findCsvColumnIndex(header, ['quantity', 'qty']);
        const nameIdx = findCsvColumnIndex(header, ['item name', 'itemname', 'description', 'desc']);
        const dueIdx = findCsvColumnIndex(header, ['due date', 'due']);
        const remarksIdx = findCsvColumnIndex(header, ['remarks', 'remark']);
        if (pnIdx < 0) {
          showToast('CSV must include "Part Number" column', 'error');
          return;
        }
        imported = lines.slice(1).map((line) => parseCsvLine(line)).map((columns) => {
          const partNumber = normalizePartNumber(columns[pnIdx]);
          if (!partNumber) return null;
          const itemName = nameIdx >= 0 ? String(columns[nameIdx] || '').trim() : '';
          const qtyRaw = qtyIdx >= 0 ? Number(columns[qtyIdx]) : 1;
          const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
          const row = {
            partNumber,
            quantity,
            itemName: itemName || partNumber,
            remarks: remarksIdx >= 0 ? String(columns[remarksIdx] || '').trim() : ''
          };
          if (dueIdx >= 0) {
            const due = parseOptionalCsvDate(columns[dueIdx]);
            if (due) row.due = due.toISOString();
          }
          return row;
        }).filter(Boolean);
      }

      if (!imported.length) {
        showToast(`No valid ${categoryLabel.toLowerCase()} rows found in CSV`, 'error');
        return;
      }

      emitChange({
        ...items,
        [categoryKey]: [...currentRows, ...imported]
      });
      showToast(`Imported ${imported.length} ${categoryLabel.toLowerCase()} row(s)`, 'success');
    } catch (error) {
      console.error('AMP check CSV import error:', error);
      showToast('Failed to import CSV', 'error');
    } finally {
      event.target.value = '';
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Required Items (copied to work order spares / consumables / tools)
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label={`Spares (${items.requiredSpares.length})`} />
        <Tab label={`Consumables (${items.requiredConsumables.length})`} />
        <Tab label={`Tools (${items.requiredToolsEquipment.length})`} />
      </Tabs>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={handleAdd}>
          Add {categoryLabel.slice(0, -1)}
        </Button>
        <Button size="small" variant="outlined" startIcon={<Download />} onClick={handleDownloadCsv}>
          Download CSV
        </Button>
        <Button size="small" variant="outlined" startIcon={<UploadFile />} onClick={handleUploadCsvClick}>
          Upload CSV
        </Button>
        <input
          ref={csvUploadInputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={handleUploadCsv}
        />
      </Box>

      {currentRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No {categoryLabel.toLowerCase()} for this check. Add manually or upload a CSV.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Part Number</TableCell>
                <TableCell width={80}>Qty</TableCell>
                <TableCell>Description</TableCell>
                {tab === 1 && <TableCell>Expiry</TableCell>}
                {tab === 2 && <TableCell>Due</TableCell>}
                <TableCell width={90}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.map((row, index) => (
                <TableRow key={`${categoryKey}-${index}`}>
                  <TableCell>
                    {tab === 1 ? row.partNumberOrSpecification : row.partNumber}
                  </TableCell>
                  <TableCell>{row.quantity || 1}</TableCell>
                  <TableCell>{row.itemName || '—'}</TableCell>
                  {tab === 1 && (
                    <TableCell>
                      {row.expiry ? dayjs(row.expiry).format('YYYY-MM-DD') : '—'}
                    </TableCell>
                  )}
                  {tab === 2 && (
                    <TableCell>
                      {row.due ? dayjs(row.due).format('YYYY-MM-DD') : '—'}
                    </TableCell>
                  )}
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(index)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(index)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editingIndex === null ? `Add ${categoryLabel.slice(0, -1)}` : `Edit ${categoryLabel.slice(0, -1)}`}
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {tab === 1 ? (
                <TextField
                  fullWidth
                  label="Part Number / Specification *"
                  value={editFormData.partNumberOrSpecification || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, partNumberOrSpecification: e.target.value })}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Part Number *"
                  value={editFormData.partNumber || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, partNumber: e.target.value })}
                />
              )}
              <TextField
                fullWidth
                label="Quantity *"
                type="number"
                inputProps={{ min: 1 }}
                value={editFormData.quantity ?? 1}
                onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
              />
              <TextField
                fullWidth
                label="Description"
                value={editFormData.itemName || ''}
                onChange={(e) => setEditFormData({ ...editFormData, itemName: e.target.value })}
              />
              {tab === 1 && (
                <DatePicker
                  label="Expiry (optional)"
                  value={editFormData.expiry || null}
                  onChange={(value) => setEditFormData({ ...editFormData, expiry: value })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
              {tab === 2 && (
                <DatePicker
                  label="Due date"
                  value={editFormData.due || null}
                  onChange={(value) => setEditFormData({ ...editFormData, due: value })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              )}
              <TextField
                fullWidth
                label="Remarks"
                value={editFormData.remarks || ''}
                onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
                multiline
                rows={2}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export { countItems, EMPTY_ITEMS };
export default AmpCheckRequiredItemsEditor;
